import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Mail } from 'lucide-react';
import NewsletterSignup from './NewsletterSignup';
import { useLanguage } from '../contexts/LanguageContext';

const Footer = () => {
  const { t, language } = useLanguage();
  
  return (
    <footer className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white border-t border-[#84bc00]/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand - tagline and social only, logo removed since header is sticky */}
          <div className="space-y-6">
            <h3 className="text-2xl font-black text-[#84bc00] tracking-tight">EXCITING.TRAVEL</h3>
            <p className="text-base text-gray-300 font-medium leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="flex space-x-5">
              <a href="#" className="text-gray-400 hover:text-[#84bc00] transition-colors duration-300 hover:scale-110">
                <Instagram size={24} />
              </a>
              <a href="#" className="text-gray-400 hover:text-[#84bc00] transition-colors duration-300 hover:scale-110">
                <Facebook size={24} />
              </a>
              <a href="#" className="text-gray-400 hover:text-[#84bc00] transition-colors duration-300 hover:scale-110">
                <Twitter size={24} />
              </a>
              <a href="#" className="text-gray-400 hover:text-[#84bc00] transition-colors duration-300 hover:scale-110">
                <Mail size={24} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-black tracking-wide text-[#84bc00] mb-6 uppercase">{t('footer.explore')}</h3>
            <ul className="space-y-4">
              <li>
                <Link to="/destinations" className="text-base text-gray-300 hover:text-white transition-colors duration-300 font-medium">
                  {t('footer.destinations')}
                </Link>
              </li>
              <li>
                <Link to="/packages" className="text-base text-gray-300 hover:text-white transition-colors duration-300 font-medium">
                  {t('footer.travelPackages')}
                </Link>
              </li>
              <li>
                <Link to="/consultation" className="text-base text-gray-300 hover:text-white transition-colors duration-300 font-medium">
                  {t('footer.bookConsultation')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Sustainability */}
          <div>
            <h3 className="text-base font-black tracking-wide text-[#84bc00] mb-6 uppercase">{t('footer.promise')}</h3>
            <ul className="space-y-4 text-base text-gray-300 font-medium">
              <li className="flex items-start">
                <span className="text-[#84bc00] mr-2 text-lg">✓</span>
                <span>{language === 'es' ? 'Hoteles eco-certificados' : 'Eco-certified hotels'}</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#84bc00] mr-2 text-lg">✓</span>
                <span>{language === 'es' ? 'Transporte carbono neutral' : 'Carbon-neutral transport'}</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#84bc00] mr-2 text-lg">✓</span>
                <span>{language === 'es' ? 'Cero plásticos de un solo uso' : 'Zero single-use plastics'}</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#84bc00] mr-2 text-lg">✓</span>
                <span>{language === 'es' ? 'Apoyo a comunidades locales' : 'Local community support'}</span>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-base font-black tracking-wide text-[#84bc00] mb-6 uppercase">{t('footer.contact')}</h3>
            <ul className="space-y-5 text-base font-medium">
              <li className="leading-relaxed">
                <span className="block text-base text-[#84bc00] mb-2 font-bold">{t('footer.email')}</span>
                <a href="mailto:hello@exciting.travel" className="text-white hover:text-[#84bc00] transition-colors text-base">
                  hello@exciting.travel
                </a>
              </li>
              <li className="leading-relaxed">
                <span className="block text-base text-[#84bc00] mb-2 font-bold">{t('footer.tollFree')}</span>
                <a href="tel:8335688090" className="text-white hover:text-[#84bc00] transition-colors text-xl font-black">
                  (833) 568-8090
                </a>
              </li>
              <li className="text-base text-white font-semibold leading-relaxed bg-[#84bc00]/10 px-3 py-2 rounded-lg border border-[#84bc00]/30">
                ✓ {t('footer.noMarketing')}
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16 pt-12 border-t border-[#84bc00]/20">
          <div className="max-w-lg mx-auto lg:mx-0">
            <NewsletterSignup variant="inline" />
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-[#84bc00]/20 mt-12 pt-10 text-center space-y-4">
          <p className="text-lg text-white font-bold">
            {language === 'es' 
              ? '© 2026 Exciting.Travel. Destinos populares. Mejores huellas.' 
              : '© 2026 Exciting.Travel. Popular destinations. Better footprints.'}
          </p>
          <div className="flex justify-center gap-6 text-base text-gray-300 font-medium">
            <Link to="/policies" className="hover:text-[#84bc00] transition-colors">
              {language === 'es' ? 'Políticas de Reserva y Cancelación' : 'Booking & Cancellation Policies'}
            </Link>
          </div>
          <p className="text-base text-gray-300 font-semibold">
            {t('footer.company')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
